import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Ticket,
  Users,
  Megaphone,
  LogOut,
  Tag,
  QrCode,
  ChevronLeft,
  ChevronRight,
  FileText,
  MessageSquare,
  CreditCard,
  ShieldCheck,
  Wallet,
  Settings,
  BanknoteIcon,
  Link2,
} from "lucide-react";
import { getStoredUser } from "../../services/api/auth";

interface AdminSidebarProps {
  onLogout: () => void;
  isCollapsed: boolean;
  toggleSidebar: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  onLogout,
  isCollapsed,
  toggleSidebar,
}) => {
  const location = useLocation();
  const currentPath = location.pathname.split("/").pop() || "dashboard";
  const user = getStoredUser();
  const userRole = user?.role || "admin";

  const allMenuItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      group: "Overview",
      roles: ["admin", "staff", "finance"],
    },
    {
      id: "checkin",
      label: "Gate Check-In",
      icon: QrCode,
      group: "Operasional",
      roles: ["admin", "staff"],
    },
    {
      id: "orders",
      label: "Riwayat Pesanan",
      icon: Ticket,
      group: "Manajemen",
      roles: ["admin", "staff", "finance"],
    },
    {
      id: "finance",
      label: "Laporan Keuangan",
      icon: Wallet,
      group: "Manajemen",
      roles: ["admin", "finance"],
    },
    {
      id: "withdrawals",
      label: "Withdraw Mitra",
      icon: BanknoteIcon,
      group: "Manajemen",
      roles: ["admin", "finance"],
    },
    {
      id: "customers",
      label: "Database Pelanggan",
      icon: Users,
      group: "Manajemen",
      roles: ["admin"],
    },
    {
      id: "tickets",
      label: "Manajemen Tiket",
      icon: Tag,
      group: "Produk",
      roles: ["admin", "staff"],
    },
    {
      id: "affiliate",
      label: "Program Affiliate",
      icon: Megaphone,
      group: "Marketing",
      roles: ["admin", "staff"],
    },
    {
      id: "blog",
      label: "Blog & SEO",
      icon: FileText,
      group: "Marketing",
      roles: ["admin", "staff"],
    },
    {
      id: "payment",
      label: "Payment Gateway",
      icon: CreditCard,
      group: "Sistem",
      roles: ["admin"],
    },
    {
      id: "license",
      label: "Lisensi & Tagihan",
      icon: ShieldCheck,
      group: "Sistem",
      roles: ["admin", "finance"],
    },
    {
      id: "linkhub",
      label: "LinkHub Settings",
      icon: Link2,
      group: "Sistem",
      roles: ["admin"],
    },
  ];

  const menuItems = allMenuItems.filter((item) =>
    item.roles.includes(userRole)
  );

  const renderGroupLabel = (group: string) => {
    if (isCollapsed) return <div className="h-4"></div>;
    return (
      <p className="px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-6 mb-2">
        {group}
      </p>
    );
  };

  let lastGroup = "";

  return (
    <aside
      className={`bg-[#0F172A] text-white flex flex-col border-r border-slate-800 h-full transition-all duration-300 relative ${
        isCollapsed ? "w-20" : "w-64"
      }`}
    >
      <div
        className={`p-4 flex items-center ${
          isCollapsed ? "justify-center" : "justify-between"
        }`}
      >
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-giz-primary to-indigo-600 rounded-lg flex items-center justify-center font-bold shadow-lg">
              G
            </div>
            <span className="font-bold text-xl tracking-tight">
              Admin<span className="text-giz-primary">Panel</span>
            </span>
          </div>
        )}
        {isCollapsed && (
          <div className="w-8 h-8 bg-gradient-to-br from-giz-primary to-indigo-600 rounded-lg flex items-center justify-center font-bold">
            G
          </div>
        )}
      </div>

      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-8 bg-white text-slate-700 border border-slate-200 rounded-full p-1 shadow-md hover:bg-slate-50 z-30 hidden md:block"
      >
        {isCollapsed ? (
          <ChevronRight className="w-3 h-3" />
        ) : (
          <ChevronLeft className="w-3 h-3" />
        )}
      </button>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto py-4 scrollbar-hide">
        {menuItems.map((item) => {
          const isNewGroup = item.group !== lastGroup;
          lastGroup = item.group;
          return (
            <div key={item.id}>
              {isNewGroup && renderGroupLabel(item.group)}
              <Link
                to={item.id === "dashboard" ? "/admin" : `/admin/${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative
                                    ${
                                      currentPath === item.id ||
                                      (item.id === "dashboard" &&
                                        location.pathname === "/admin")
                                        ? "bg-giz-primary text-white shadow-md"
                                        : "text-slate-400 hover:bg-slate-800 hover:text-white"
                                    }`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0`} />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <button
          onClick={onLogout}
          className={`w-full flex items-center ${
            isCollapsed ? "justify-center" : "gap-3"
          } px-3 py-2 text-rose-400 hover:bg-rose-50/10 rounded-lg text-sm font-bold transition-colors`}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && "Logout"}
        </button>
      </div>
    </aside>
  );
};
